import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';
import { UserPreferences, UserPreferencesResponse, UserQueryResponse, UserViewModel } from '../models/user.model';
import { USER_PREFERENCES_QUERY } from '../shared/graphql/user-preferences.query';
import { USER_QUERY } from '../shared/graphql/user.query';
import { ApiEndpointsService } from './api-endpoints.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiEndpointsService);
  private readonly endpoint = this.apiService.baseUrl;

  private userCache$?: Observable<UserQueryResponse>;

  getUserFirstName(): Observable<UserViewModel> {
    return this.getUser().pipe(
      map((response) => {
        const firstName = response.data.user.profile.names[0].name.first_name;
        const organizationName = response.data.user.profile.organizations.find((org) => org.primary == true)?.display_labels;
        return { firstName, organizationName };
      }),
      catchError((err) => {
        console.error('Failed to load user first name', err);
        return of({ firstName: 'Guest', organizationName: undefined });
      })
    );
  }

  getUserId(): Observable<string> {
    return this.getUser().pipe(
      map((response) => {
        return response.data.user.user_id;
      }),
      catchError((err) => {
        console.error('Failed to load user id', err);
        return of('no_user_id');
      })
    );
  }

  private getUser(): Observable<UserQueryResponse> {
    if (this.userCache$) {
      return this.userCache$;
    }

    const request$ = this.http.post<UserQueryResponse>(this.endpoint, { query: USER_QUERY }, { withCredentials: true }).pipe(shareReplay(1));

    this.userCache$ = request$;

    return request$;
  }

  clearUserCache(): void {
    this.userCache$ = undefined;
  }

  refreshUser(): Observable<UserQueryResponse> {
    this.clearUserCache();
    return this.getUser();
  }

  getUserPreferences(): Observable<UserPreferences> {
    return this.http
      .post<UserPreferencesResponse>(this.endpoint, { query: USER_PREFERENCES_QUERY }, { withCredentials: true })
      .pipe(shareReplay(1))
      .pipe(
        map((r: UserPreferencesResponse) => {
          const prefs = r?.data?.user?.preferences ?? {};
          const locale = prefs.default_locale;
          const timezone = prefs.default_time_zone;
          const unitSystem = prefs.default_unit_system;
          return { locale, timezone, unitSystem };
        })
      );
  }
}
